/**
 * Sticky sale bar that appears after the user scrolls past a threshold.
 *
 * Attributes:
 * - data-position: "top" | "bottom"
 * - data-scroll-threshold: number (px)
 * - data-storage-key: localStorage key for dismiss state
 * - data-end: optional end datetime (same formats as countdown-timer)
 */
class StickySaleBar extends HTMLElement {
  /** @type {boolean} */
  #dismissed = false;

  /** @type {number} */
  #threshold = 200;

  /** @type {number | null} */
  #endMs = null;

  /** @type {boolean} */
  #ticking = false;

  connectedCallback() {
    if (this.dataset.designMode === 'true') {
      this.#show();
      return;
    }

    this.#threshold = Number(this.dataset.scrollThreshold || 200);
    this.#endMs = this.#parseEnd(this.dataset.end);

    const storageKey = this.dataset.storageKey;
    if (storageKey && window.localStorage?.getItem(storageKey) === '1') {
      this.#dismissed = true;
      this.hidden = true;
      return;
    }

    if (this.#endMs != null && Date.now() >= this.#endMs) {
      this.hidden = true;
      return;
    }

    this.hidden = true;
    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });

    const dismiss = this.querySelector('[data-sticky-sale-dismiss]');
    dismiss?.addEventListener('click', this.#onDismiss);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.querySelector('[data-sticky-sale-dismiss]')?.removeEventListener('click', this.#onDismiss);
    this.#clearBodyPad();
  }

  #onDismiss = () => {
    this.#dismissed = true;
    this.#hide();

    const storageKey = this.dataset.storageKey;
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, '1');
      } catch {
        // Ignore storage failures (private mode, etc.)
      }
    }
  };

  #onScroll = () => {
    if (this.#ticking || this.#dismissed) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      this.#ticking = false;

      if (this.#endMs != null && Date.now() >= this.#endMs) {
        this.#hide();
        return;
      }

      if (window.scrollY >= this.#threshold) {
        this.#show();
      } else {
        this.#hide();
      }
    });
  };

  #show() {
    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-visible');
      this.#setBodyPad();
    });
  }

  #hide() {
    this.classList.remove('is-visible');
    this.#clearBodyPad();

    // Keep in DOM for animation, then hide once transition ends when not design mode
    if (this.dataset.designMode === 'true') return;

    window.setTimeout(() => {
      if (!this.classList.contains('is-visible')) {
        this.hidden = true;
      }
    }, 280);
  }

  #setBodyPad() {
    if (this.dataset.position !== 'bottom') return;
    if (!this.classList.contains('is-visible')) return;

    const height = Math.ceil(this.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--sticky-sale-bar-pad', `${height}px`);
  }

  #clearBodyPad() {
    document.documentElement.style.removeProperty('--sticky-sale-bar-pad');
  }

  /**
   * @param {string | undefined} value
   * @returns {number | null}
   */
  #parseEnd(value) {
    if (!value) return null;
    const normalized = value.trim().replace(' ', 'T');
    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }
}

if (!customElements.get('sticky-sale-bar')) {
  customElements.define('sticky-sale-bar', StickySaleBar);
}
